import React from 'react'
import { HelpCircle } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../components/select'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { UITooltip } from '../overlay/ui-tooltip'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

// Base Select exports - for other composed components
export {
  Select as UISelectBase,
  SelectContent as UISelectContentBase,
  SelectGroup as UISelectGroupBase,
  SelectItem as UISelectItemBase,
  SelectLabel as UISelectLabelBase,
  SelectSeparator as UISelectSeparatorBase,
  SelectTrigger as UISelectTriggerBase,
  SelectValue as UISelectValueBase,
} from '../../components/select'

export interface UISelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface UISelectOptionGroup {
  label?: string
  options: UISelectOption[]
}

export interface UISelectProps extends React.ComponentProps<typeof Select> {
  /**
   * 라벨
   */
  label?: string
  /**
   * 라벨 ID
   */
  labelId?: string
  /**
   * Optional help tooltip rendered as info icon next to the label.
   */
  labelHelp?: React.ReactNode
  /**
   * 설명 텍스트
   */
  description?: string
  /**
   * 오류 메시지
   */
  error?: string
  /**
   * 도움말 텍스트
   */
  helperText?: string
  /**
   * 필수 여부
   */
  required?: boolean
  /**
   * 플레이스홀더
   */
  placeholder?: string
  /**
   * 컨테이너 클래스명
   */
  containerClassName?: string
  /**
   * 트리거 크기
   */
  size?: 'sm' | 'default'
  /**
   * 옵션 목록 (단순한 형태)
   */
  options?: UISelectOption[]
  /**
   * 그룹화된 옵션 목록
   */
  groups?: UISelectOptionGroup[]
  /**
   * 접근성 라벨
   */
  'aria-label'?: string
  /**
   * 접근성 라벨 참조
   */
  'aria-labelledby'?: string
  /**
   * 접근성 설명 참조
   */
  'aria-describedby'?: string
}

export interface UISelectTriggerProps extends React.ComponentProps<typeof SelectTrigger> {}
export interface UISelectContentProps extends React.ComponentProps<typeof SelectContent> {}
export interface UISelectItemProps extends React.ComponentProps<typeof SelectItem> {}
export interface UISelectValueProps extends React.ComponentProps<typeof SelectValue> {}
export interface UISelectLabelProps extends React.ComponentProps<typeof SelectLabel> {}
export interface UISelectGroupProps extends React.ComponentProps<typeof SelectGroup> {}
export interface UISelectSeparatorProps extends React.ComponentProps<typeof SelectSeparator> {}

/**
 * 셀렉트 컴포넌트
 * 라벨, 도움말 텍스트, 오류 메시지, 옵션 그룹화를 지원합니다.
 */
export const UISelect = React.forwardRef<HTMLDivElement, UISelectProps>(({
  label,
  labelId,
  labelHelp,
  description,
  error,
  helperText,
  required = false,
  placeholder,
  containerClassName,
  size = "default",
  options,
  groups,
  children,
  ...props
}, ref) => {
  const selectId = labelId || `select-${React.useId()}`

  // Extract accessibility props that need to go to the trigger
  const {
    'aria-label': ariaUILabel,
    'aria-labelledby': ariaUILabelledBy,
    'aria-describedby': ariaDescribedBy,
    ...selectProps
  } = props

  // Resolve selected option label for display in SelectValue.
  // Radix Select renders SelectContent inside a Portal which only mounts
  // when the dropdown is open. SelectValue relies on SelectItemText to
  // portal the display text, but if the dropdown has never been opened,
  // the text isn't available. By passing the label as children to
  // SelectValue, the selected value is always visible.
  const selectedLabel = React.useMemo(() => {
    const value = selectProps.value as string | undefined
    if (!value) return undefined

    if (options) {
      const found = options.find((o) => o.value === value)
      if (found) return found.label
    }

    if (groups) {
      for (const group of groups) {
        const found = group.options.find((o) => o.value === value)
        if (found) return found.label
      }
    }

    return undefined
  }, [selectProps.value, options, groups])

  const renderContent = () => {
    if (children) {
      return children
    }

    if (groups) {
      return groups.map((group, groupIndex) => (
        <UISelectGroup key={group.label || `group-${groupIndex}`}>
          {group.label && <UISelectLabel>{group.label}</UISelectLabel>}
          {group.options.map((option) => (
            <UISelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </UISelectItem>
          ))}
          {groupIndex < groups.length - 1 && <UISelectSeparator key={`separator-${groupIndex}`} />}
        </UISelectGroup>
      ))
    }

    if (options) {
      return options.map((option) => (
        <UISelectItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </UISelectItem>
      ))
    }

    return null
  }

  if (!label && !description && !error && !helperText) {
    const selectElement = (
      <Select {...selectProps}>
        <UISelectTrigger
          size={size}
          aria-label={ariaUILabel}
          aria-labelledby={ariaUILabelledBy}
          aria-describedby={ariaDescribedBy}
        >
          <UISelectValue placeholder={placeholder}>{selectedLabel ?? null}</UISelectValue>
        </UISelectTrigger>
        <UISelectContent>
          {renderContent()}
        </UISelectContent>
      </Select>
    )

    // If containerClassName is provided, wrap in UIBox
    if (containerClassName) {
      return (
        <UIBox ref={ref} className={containerClassName}>
          {selectElement}
        </UIBox>
      )
    }

    return (
      <UIBox ref={ref}>
        {selectElement}
      </UIBox>
    )
  }

  return (
    <UIBox ref={ref} className={cn("space-y-2", containerClassName)}>
      {label && (
        labelHelp ? (
          <UIBox className="flex items-center gap-1">
            <UILabel
              htmlFor={selectId}
              className={cn(
                "text-sm font-medium leading-none",
                required && "after:content-['*'] after:ml-0.5 after:text-destructive",
                error && "text-destructive"
              )}
            >
              {label}
            </UILabel>
            <UITooltip content={labelHelp} side="top">
              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
            </UITooltip>
          </UIBox>
        ) : (
          <UILabel
            htmlFor={selectId}
            className={cn(
              "text-sm font-medium leading-none",
              required && "after:content-['*'] after:ml-0.5 after:text-destructive",
              error && "text-destructive"
            )}
          >
            {label}
          </UILabel>
        )
      )}

      {description && (
        <UITypography className="text-xs text-muted-foreground">{description}</UITypography>
      )}

      <Select {...selectProps}>
        <UISelectTrigger
          id={selectId}
          size={size}
          className={cn(
            error && "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20"
          )}
          aria-invalid={error ? 'true' : undefined}
          aria-label={ariaUILabel}
          aria-labelledby={ariaUILabelledBy}
          aria-describedby={
            ariaDescribedBy ||
            (error ? `${selectId}-error` :
            helperText ? `${selectId}-helper` :
            undefined)
          }
        >
          <UISelectValue placeholder={placeholder}>{selectedLabel ?? null}</UISelectValue>
        </UISelectTrigger>
        <UISelectContent>
          {renderContent()}
        </UISelectContent>
      </Select>

      {error && (
        <UITypography
          as="p"
          id={`${selectId}-error`}
          className="text-xs text-destructive"
          role="alert"
        >
          {error}
        </UITypography>
      )}

      {helperText && !error && (
        <UITypography
          as="p"
          id={`${selectId}-helper`}
          className="text-xs text-muted-foreground"
        >
          {helperText}
        </UITypography>
      )}
    </UIBox>
  )
})

export const UISelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectTrigger>,
  UISelectTriggerProps
>(({ className, ...props }, ref) => (
  <SelectTrigger ref={ref} className={className} {...props} />
))

export const UISelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectContent>,
  UISelectContentProps
>(({ className, ...props }, ref) => (
  <SelectContent ref={ref} className={className} {...props} />
))

export const UISelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectItem>,
  UISelectItemProps
>(({ className, ...props }, ref) => (
  <SelectItem ref={ref} className={className} {...props} />
))

export const UISelectValue = React.forwardRef<
  React.ComponentRef<typeof SelectValue>,
  UISelectValueProps
>(({ className, ...props }, ref) => (
  <SelectValue ref={ref} className={className} {...props} />
))

export const UISelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectLabel>,
  UISelectLabelProps
>(({ className, ...props }, ref) => (
  <SelectLabel ref={ref} className={className} {...props} />
))

export const UISelectGroup = React.forwardRef<
  React.ComponentRef<typeof SelectGroup>,
  UISelectGroupProps
>(({ className, ...props }, ref) => (
  <SelectGroup ref={ref} className={className} {...props} />
))

export const UISelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectSeparator>,
  UISelectSeparatorProps
>(({ className, ...props }, ref) => (
  <SelectSeparator ref={ref} className={className} {...props} />
))

UISelect.displayName = "UISelect"
UISelectTrigger.displayName = "UISelectTrigger"
UISelectContent.displayName = "UISelectContent"
UISelectItem.displayName = "UISelectItem"
UISelectValue.displayName = "UISelectValue"
UISelectLabel.displayName = "UISelectLabel"
UISelectGroup.displayName = "UISelectGroup"
UISelectSeparator.displayName = "UISelectSeparator"